// Pair each document with its OWN component files, by name.
//
// The legacy half of an export is one tree JSON per document plus a
// handful of component JSONs beside it:
//
//   Project1.json.gz
//   Project1_AdditionalParam.json.gz
//   rst_basic_sample_project.json.gz
//   rst_basic_sample_project_AdditionalParam.json.gz
//
// The viewers used to walk that list positionally: every `.json` entry
// counted as a document, and `jsons[i]` was paired with
// `additionalParams[i]`. Those indices line up only when there is exactly
// ONE document. Measured on two real 3-document exports in production
// (AUS_SITE_ALL_ARCH_TSLA, Tesla Model2), the loop ran six times for
// three documents and paired document 2's tree with document 3's
// parameters.
//
// Two things kept that from being visible. `generateRawMeshes` and
// `generateRawTree` dereference `json.SubProjects`, which a parameter
// payload does not have, so the odd iterations threw inside a `.then()`
// whose `.catch(() => {})` swallowed it; and `setAdditionalMeshes(...arr)`
// spread an array into a one-argument setState, so only element 0 — which
// happened to be document 1's correct parameters — ever reached state.
// Net symptom: only the first document had parameter filters.
//
// Which is why the obvious one-line fix is a trap. Removing the spread
// alone UNMASKS the mispairing and starts handing documents 2..N another
// document's parameters. Wrong data presented confidently is worse than
// absent data, so the pairing has to be keyed on document identity and
// both have to change together.
//
// Matching by name rather than by position is what this module does.

// Suffixes the exporter appends to a document's base name. `_ViewSheets`
// is produced but has no consumer in this codebase; it is listed so it is
// recognised as a component and never mistaken for a document.
export const COMPONENT_SUFFIXES = [
  '_AdditionalParam',
  // Room boundaries in the export frame, written next to the document's
  // records (the plugin's "<Document>_Rooms.json"). Listed here so it
  // pairs with its document instead of standing as a document of its own.
  '_Rooms',
  // Building grids and levels, written UNCONDITIONALLY beside the
  // records - [] means "asked, none found", which is a different fact
  // from a missing file and is why they are always written
  // (desktop-agent, 2026-09-21). Listed here BEFORE they are uploaded:
  // without the pairing the viewer reads <Doc>_Grids.json as a DOCUMENT
  // of its own, and a project would grow phantom documents the moment
  // the export lands.
  '_Grids',
  '_Levels',
  '_ElectricalCircuits',
  '_MechanicalSystems',
  '_PipingSystems',
  '_SwitchSystems',
  '_ViewSheets',
];

// Field on the returned set for each suffix.
const FIELD_BY_SUFFIX = {
  _AdditionalParam: 'additionalParam',
  _Rooms: 'rooms',
  _Grids: 'grids',
  _Levels: 'levels',
  _ElectricalCircuits: 'electricalCircuits',
  _MechanicalSystems: 'mechanicalSystems',
  _PipingSystems: 'pipingSystems',
  _SwitchSystems: 'switchSystems',
  _ViewSheets: 'viewSheets',
};

const isJson = (name) => /\.json(\.gz)?$/i.test(String(name || ''));
const baseOf = (name) => String(name || '').replace(/\.json(\.gz)?$/i, '');

// Which component is this, if any? Returns { suffix, docBase } or null.
// Derived files the exporter writes next to a document's records
// (2026-09-09): "<Document>_TreeIndex" (hierarchy + counts, no per-element
// data) and "<Document>_AdditionalParam.<Token>" (the parameter rows of
// one top category; "Other" = rows the record does not list). They
// belong to the document, never to a set of their own - before this
// rule each of SpaceX's 147 derived files was fetched and parsed as a
// document tree at open.
const TREE_INDEX_SUFFIX = '_TreeIndex';
const PARAM_SLICE_RE = /^(.*)_AdditionalParam\.([A-Za-z0-9_-]+)$/;
// "<Document>_TreeChunk.<idx>" (2026-09-13): the record tree cut into
// fixed-size pieces by a hash of the UniqueId, so the worker parses one
// piece per question instead of the whole tree. Which piece holds which
// element is decided by `recordChunks` in the document's _TreeIndex.
const TREE_CHUNK_RE = /^(.*)_TreeChunk\.(\d+)$/;

function componentOf(base) {
  if (base.length > TREE_INDEX_SUFFIX.length && base.endsWith(TREE_INDEX_SUFFIX)) {
    return { suffix: TREE_INDEX_SUFFIX, docBase: base.slice(0, -TREE_INDEX_SUFFIX.length), field: 'treeIndex' };
  }
  const chunk = TREE_CHUNK_RE.exec(base);
  if (chunk) {
    return { suffix: '_TreeChunk.' + chunk[2], docBase: chunk[1], field: 'treeChunks', index: Number(chunk[2]) };
  }
  const slice = PARAM_SLICE_RE.exec(base);
  if (slice) {
    return { suffix: '_AdditionalParam.' + slice[2], docBase: slice[1], field: 'paramSlices', token: slice[2] };
  }
  for (const suffix of COMPONENT_SUFFIXES) {
    if (base.length > suffix.length && base.endsWith(suffix)) {
      return { suffix, docBase: base.slice(0, -suffix.length) };
    }
  }
  return null;
}

// mapping[] and paths[] are the index-aligned arrays from zipInfo, with
// the streaming-pipeline entries already removed by the caller.
//
// Returns one entry per DOCUMENT, in the order the documents appear:
//   { name, tree, additionalParam, rooms, electricalCircuits,
//     mechanicalSystems, pipingSystems, switchSystems, viewSheets }
// where every value but `name` is a path or null.
export function buildDocumentSets(mapping, paths) {
  const names = Array.isArray(mapping) ? mapping : [];
  const uris = Array.isArray(paths) ? paths : [];

  const jsonEntries = [];
  for (let i = 0; i < names.length; i++) {
    if (!isJson(names[i])) continue;
    jsonEntries.push({ base: baseOf(names[i]), path: uris[i] });
  }

  // Pass one: every entry that is not a recognised component is a
  // document. Done first so pass two can tell whether a component's
  // document actually exists.
  const order = [];
  const byBase = new Map();
  for (const e of jsonEntries) {
    if (componentOf(e.base)) continue;
    if (byBase.has(e.base)) continue;
    const set = {
      name: e.base,
      tree: e.path,
      treeIndex: null,
      paramSlices: null,
      treeChunks: null,
      additionalParam: null,
      rooms: null,
      grids: null,
      levels: null,
      electricalCircuits: null,
      mechanicalSystems: null,
      pipingSystems: null,
      switchSystems: null,
      viewSheets: null,
    };
    byBase.set(e.base, set);
    order.push(set);
  }

  // Pass two: attach each component to its document.
  for (const e of jsonEntries) {
    const comp = componentOf(e.base);
    if (!comp) continue;
    const set = byBase.get(comp.docBase);
    if (!set) {
      // A derived index/slice without its document tree is dropped: it
      // is never a document of its own.
      if (comp.field) continue;
      // A component whose document is missing. Do NOT silently drop it:
      // a document genuinely named "..._AdditionalParam" would otherwise
      // vanish from the model. Treat it as a document of its own, which
      // is what the old positional walk did by accident and is the safe
      // direction when the naming is ambiguous.
      const own = {
        name: e.base,
        tree: e.path,
        additionalParam: null,
        rooms: null,
        grids: null,
        levels: null,
        electricalCircuits: null,
        mechanicalSystems: null,
        pipingSystems: null,
        switchSystems: null,
        viewSheets: null,
      };
      byBase.set(e.base, own);
      order.push(own);
      continue;
    }
    if (comp.field === 'treeIndex') { if (!set.treeIndex) set.treeIndex = e.path; continue; }
    if (comp.field === 'treeChunks') {
      if (!set.treeChunks) set.treeChunks = {};
      if (!set.treeChunks[comp.index]) set.treeChunks[comp.index] = e.path;
      continue;
    }
    if (comp.field === 'paramSlices') {
      if (!set.paramSlices) set.paramSlices = {};
      if (!set.paramSlices[comp.token]) set.paramSlices[comp.token] = e.path;
      continue;
    }
    const field = FIELD_BY_SUFFIX[comp.suffix];
    if (field && !set[field]) set[field] = e.path;
  }

  return order;
}

// The component payloads are flat arrays of records keyed by UniqueId —
// verified against a real export: an `_AdditionalParam.json` is an ARRAY
// whose records carry Name, UniqueId, SharedParameters,
// EquipmentParameters, ProjectParameters, FamilyParameters,
// GlobalParameters, BuiltInParameters.
//
// Consumers look these up with `.find((j) => j.UniqueId === …)`, so the
// right shape for N documents is every document's records CONCATENATED,
// not one document's. For a single document this is exactly what the old
// code produced, which is why the fix does not disturb the common case.
//
// Appended one record at a time, never `out.push(...chunk)`. A spread
// passes every element as a call argument, and the engine's argument
// limit is a few hundred thousand: the SpaceX BA03 host alone carries
// 1,068,750 _AdditionalParam records, so the spread threw "Maximum call
// stack size exceeded" inside the viewer's onLoad, the models tree never
// rendered, and the 3D streamed on beside two empty panels (2026-09-08).
export function flattenRecords(perDocument) {
  const out = [];
  for (const chunk of perDocument || []) {
    if (Array.isArray(chunk)) {
      for (let i = 0; i < chunk.length; i++) out.push(chunk[i]);
    } else if (chunk) {
      out.push(chunk);
    }
  }
  return out;
}
